import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Chip,
    CircularProgress,
    TextField,
    Tabs,
    Tab,
    Card,
    CardContent,
    CardActionArea,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Slider,
    InputAdornment,
    IconButton,
    Tooltip,
    Alert,
    LinearProgress,
    Divider,
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    EmojiEvents as TrophyIcon,
    BarChart as BarChartIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { InkColorIcons } from '../Tracker/InkColorIcon';
import { httpRequest } from '../../services/http-client';

// DB stores lowercase; InkColorIcon expects Title-case.
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
const toIconColors = (colors) => [...colors].sort().map(capitalize);
const comboKey = (colors) => [...colors].sort().join('+');
const inkLabel = (color) => capitalize(color);

const fmtDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' })
        + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
};

// Small inline ink icons with tooltip showing color names
const InkCombo = ({ colors = [], size = 20 }) => {
    const sorted = toIconColors(colors);
    if (!sorted.length) return null;
    return (
        <Tooltip title={sorted.join(' / ')} arrow>
            <Box sx={{ display: 'inline-flex' }}>
                <InkColorIcons colors={sorted} size={size} gap={0.25} tooltip={false} />
            </Box>
        </Tooltip>
    );
};

// ---- HOUR OPTIONS ----
const HOUR_OPTIONS = [
    { label: '1h', value: 1 },
    { label: '4h', value: 4 },
    { label: '24h', value: 24 },
    { label: '7d', value: 168 },
];

// ---- META TAB ----
const MetaTab = ({ selectedQueue }) => {
    const theme = useTheme();
    const [hours, setHours] = useState(1);
    const [mmrMin, setMmrMin] = useState(0);
    const [mmrMinDebounced, setMmrMinDebounced] = useState(0);
    const sliderDebounceRef = useRef(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSliderChange = (_, value) => {
        setMmrMin(value);
        clearTimeout(sliderDebounceRef.current);
        sliderDebounceRef.current = setTimeout(() => setMmrMinDebounced(value), 500);
    };

    const fetchMeta = useCallback(() => {
        setLoading(true);
        setError(null);
        const query = { hours };
        if (selectedQueue) query.queue = selectedQueue;
        if (mmrMinDebounced > 0) query.mmr_min = mmrMinDebounced;

        httpRequest('/v1/scouting/meta', { query, cacheTtlMs: 60000 })
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [selectedQueue, hours, mmrMinDebounced]);

    useEffect(() => { fetchMeta(); }, [fetchMeta]);

    const maxCount = data?.combos?.[0]?.count || 1;

    return (
        <Box>
            {/* Tab-local filters */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {HOUR_OPTIONS.map((o) => (
                        <Chip
                            key={o.value}
                            label={o.label}
                            onClick={() => setHours(o.value)}
                            variant={hours === o.value ? 'filled' : 'outlined'}
                            color={hours === o.value ? 'primary' : 'default'}
                            size="small"
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ mb: 3, px: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Min MMR: {mmrMin === 0 ? 'all players' : `≥ ${mmrMin}`}
                </Typography>
                <Slider
                    value={mmrMin}
                    onChange={handleSliderChange}
                    min={0}
                    max={1800}
                    step={50}
                    marks={[
                        { value: 0, label: '0' },
                        { value: 1000, label: '1000' },
                        { value: 1200, label: '1200' },
                        { value: 1400, label: '1400' },
                        { value: 1600, label: '1600' },
                    ]}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{ maxWidth: 400 }}
                />
            </Box>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {data && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {data.total} entries · last {hours < 24 ? `${hours}h` : `${hours / 24}d`}
                    {selectedQueue ? ` · ${selectedQueue}` : ''}
                    {mmrMinDebounced > 0 ? ` · MMR ≥ ${mmrMinDebounced}` : ''}
                </Typography>
            )}

            {data?.combos?.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {data.combos.map((combo, i) => (
                        <Card key={comboKey(combo.colors)} variant="outlined" sx={{
                            background: 'rgba(255,255,255,0.03)',
                            borderColor: 'rgba(255,255,255,0.08)',
                        }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ width: 28, textAlign: 'right', flexShrink: 0 }}>
                                        #{i + 1}
                                    </Typography>
                                    <InkColorIcons colors={toIconColors(combo.colors)} size={22} gap={0.5} tooltip={false} sx={{ flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                        {toIconColors(combo.colors).join(' / ')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                                        {combo.count}×
                                    </Typography>
                                    <Typography variant="body1" fontWeight={700} sx={{ width: 52, textAlign: 'right', flexShrink: 0 }}>
                                        {combo.pct}%
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, height: 4, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${(combo.count / maxCount) * 100}%`,
                                        bgcolor: theme.palette.primary.main,
                                        borderRadius: 2,
                                        transition: 'width 0.4s',
                                    }} />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {data && data.combos.length === 0 && !loading && (
                <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                    No data for selected filters.
                </Typography>
            )}
        </Box>
    );
};

// ---- PLAYER TAB ----
const PlayerTab = ({ selectedQueue, initialUserId, onClearInitial }) => {
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (initialUserId) loadProfile(initialUserId);
    }, [initialUserId]);

    const handleSearch = (value) => {
        setQuery(value);
        clearTimeout(debounceRef.current);
        if (value.trim().length < 2) { setSearchResults(null); return; }
        debounceRef.current = setTimeout(() => {
            setSearching(true);
            httpRequest('/v1/scouting/players/search', { query: { name: value.trim() } })
                .then((r) => setSearchResults(r.players || []))
                .catch(() => setSearchResults([]))
                .finally(() => setSearching(false));
        }, 400);
    };

    const loadProfile = (userId) => {
        setProfileLoading(true);
        setProfileError(null);
        setProfile(null);
        setSearchResults(null);
        setQuery('');
        httpRequest(`/v1/scouting/players/${encodeURIComponent(userId)}`, { cacheTtlMs: 30000 })
            .then(setProfile)
            .catch((e) => setProfileError(e.message))
            .finally(() => setProfileLoading(false));
    };

    const clearProfile = () => {
        setProfile(null);
        setProfileError(null);
        if (onClearInitial) onClearInitial();
    };

    // Filter profile data by selected queue (client-side)
    const visibleCombos = profile
        ? (selectedQueue
            ? (profile.byQueue.find((q) => q.queue === selectedQueue)?.combos || [])
            : profile.combos)
        : [];

    const visibleGames = profile
        ? (selectedQueue
            ? profile.recentGames.filter((g) => g.queue_short_name === selectedQueue)
            : profile.recentGames)
        : [];

    return (
        <Box>
            {!profile && (
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search player by name…"
                        value={query}
                        onChange={(e) => handleSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                                </InputAdornment>
                            ),
                            endAdornment: query ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setQuery(''); setSearchResults(null); }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    {searchResults !== null && (
                        <Paper variant="outlined" sx={{ mt: 1, borderColor: 'rgba(255,255,255,0.1)' }}>
                            {searchResults.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" color="text.secondary">No results.</Typography>
                                </Box>
                            ) : searchResults.map((p) => (
                                <CardActionArea key={p.user_id} onClick={() => loadProfile(p.user_id)}>
                                    <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.user_id}</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            {p.mmr && <Chip label={`${Math.round(p.mmr)} MMR`} size="small" sx={{ mr: 1 }} />}
                                            <Typography variant="caption" color="text.secondary">
                                                {p.last_seen_at ? fmtDate(p.last_seen_at) : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                </CardActionArea>
                            ))}
                        </Paper>
                    )}
                </Box>
            )}

            {profileLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
            {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}

            {profile && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h5" fontWeight={700}>{profile.currentName}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {profile.userId}
                            </Typography>
                            {profile.noDbData && (
                                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                                    No match history in our database yet — player hasn't been seen in live games.
                                </Typography>
                            )}
                                    {profile.aliases?.length > 1 && (
                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">Also known as:</Typography>
                                    {profile.aliases.slice(1, 5).map((a) => (
                                        <Chip key={a.name} label={a.name} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            )}
                        </Box>
                        <Chip label="← Back" onClick={clearProfile} variant="outlined" size="small" />
                    </Box>

                    {/* Combo summary */}
                    {visibleCombos.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Decks {selectedQueue ? `· ${selectedQueue}` : '(all-time)'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {visibleCombos.map((c) => (
                                    <Tooltip key={comboKey(c.colors)} title={`${toIconColors(c.colors).join(' / ')} · ${c.count}×`}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2, px: 1, py: 0.5 }}>
                                            <InkColorIcons colors={toIconColors(c.colors)} size={18} gap={0.25} tooltip={false} />
                                            <Typography variant="caption" color="text.secondary">{c.count}×</Typography>
                                        </Box>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Per-queue breakdown (only when no queue filter) */}
                    {!selectedQueue && profile.byQueue?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">By format</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {profile.byQueue.map((q) => (
                                    <Card key={q.queue} variant="outlined" sx={{ minWidth: 150, background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={600}>
                                                {q.queue}
                                            </Typography>
                                            {q.combos.slice(0, 3).map((c) => (
                                                <Box key={comboKey(c.colors)} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <InkColorIcons colors={toIconColors(c.colors)} size={16} gap={0.25} tooltip={false} />
                                                    <Typography variant="caption" color="text.secondary">{c.count}×</Typography>
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Recent games */}
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        Recent games ({visibleGames.length}{selectedQueue ? ` in ${selectedQueue}` : ''})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Format</TableCell>
                                    <TableCell>Colors</TableCell>
                                    <TableCell align="right">MMR</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleGames.map((g) => (
                                    <TableRow key={g.game_id} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>
                                            <Typography variant="caption">{fmtDate(g.started_at)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={g.queue_short_name} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            {g.ink_colors?.length > 0
                                                ? <InkColorIcons colors={toIconColors(g.ink_colors)} size={16} gap={0.25} tooltip={false} />
                                                : <Typography variant="caption" color="text.secondary">—</Typography>
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption">{g.mmr ? Math.round(g.mmr) : '—'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {visibleGames.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Typography variant="caption" color="text.secondary">No games found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {!profile && !profileLoading && searchResults === null && (
                <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                    Enter a player name to search.
                </Typography>
            )}
        </Box>
    );
};

// ---- RANKING TAB ----
const RankingTab = ({ selectedQueue, onSelectPlayer }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (data) setRefreshing(true);
        else setLoading(true);
        setError(null);
        const query = selectedQueue ? { queue: selectedQueue } : {};
        httpRequest('/v1/scouting/leaderboard', { query, cacheTtlMs: 120000 })
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => { setLoading(false); setRefreshing(false); });
    }, [selectedQueue]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading && !data) return <LinearProgress sx={{ borderRadius: 1 }} />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return null;

    const { leaderboard, queueName } = data;

    const topCombo = (combos, field) =>
        [...combos].sort((a, b) => b[field] - a[field]).find((c) => c[field] > 0) || null;

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <TrophyIcon fontSize="small" color="warning" />
                {queueName && (
                    <Typography variant="subtitle2" color="text.secondary">{queueName}</Typography>
                )}
                <Chip label={`${leaderboard.length} players`} size="small" variant="outlined" />
                <Chip label="official ranking · duels.ink" size="small" variant="outlined" color="success" />
                {refreshing && <CircularProgress size={14} />}
            </Box>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderColor: 'rgba(255,255,255,0.08)', opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.2s' }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 36, px: 1 }}>#</TableCell>
                            <TableCell>Player</TableCell>
                            <TableCell align="right">MMR</TableCell>
                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>W–L</TableCell>
                            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>WR%</TableCell>
                            <TableCell align="center">1d</TableCell>
                            <TableCell align="center">7d</TableCell>
                            <TableCell align="center">All-time</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaderboard.map((p) => {
                            const top1d = topCombo(p.combos, 'count_1d');
                            const top7d = topCombo(p.combos, 'count_7d');
                            const topAll = topCombo(p.combos, 'total');

                            return (
                                <TableRow
                                    key={p.userId}
                                    hover
                                    sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                                    onClick={() => onSelectPlayer(p.userId)}
                                >
                                    <TableCell sx={{ px: 1 }}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={p.rank <= 3 ? 700 : 400}
                                            color={p.rank <= 3 ? 'warning.main' : 'text.secondary'}
                                        >
                                            {p.rank}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>{p.username}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={600}>{Math.round(p.mmr)}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {p.wins != null ? `${p.wins}–${p.losses}` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                        <Typography variant="caption">
                                            {p.winRate != null ? `${Number(p.winRate).toFixed(0)}%` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {top1d
                                            ? <Tooltip title={`${toIconColors(top1d.colors).join(' / ')} · ${top1d.count_1d}× last 24h`} arrow><Box sx={{ display: 'inline-flex' }}><InkCombo colors={top1d.colors} size={18} /></Box></Tooltip>
                                            : <Typography variant="caption" color="text.secondary">—</Typography>
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        {top7d
                                            ? <Tooltip title={`${toIconColors(top7d.colors).join(' / ')} · ${top7d.count_7d}× last 7d`} arrow><Box sx={{ display: 'inline-flex' }}><InkCombo colors={top7d.colors} size={18} /></Box></Tooltip>
                                            : <Typography variant="caption" color="text.secondary">—</Typography>
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        {topAll
                                            ? <Tooltip title={`${toIconColors(topAll.colors).join(' / ')} · ${topAll.total}× all-time`} arrow><Box sx={{ display: 'inline-flex' }}><InkCombo colors={topAll.colors} size={18} /></Box></Tooltip>
                                            : <Typography variant="caption" color="text.secondary">—</Typography>
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const QUEUES = [
    { key: 'S12-BO1', label: 'Core BO1' },
    { key: 'S12-BO3', label: 'Core BO3' },
    { key: 'INF-BO1', label: 'Infinity BO1' },
    { key: 'INF-BO3', label: 'Infinity BO3' },
];

// ---- MAIN ----
const Scouting = () => {
    const [tab, setTab] = useState(0);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedQueue, setSelectedQueue] = useState('S12-BO1');

    const handleSelectPlayer = (userId) => {
        setSelectedUserId(userId);
        setTab(1);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h4" fontWeight={700} gutterBottom>Scouting</Typography>
                <Typography variant="body2" color="text.secondary">
                    Live meta from duels.ink — updated every minute.
                </Typography>
            </Box>

            {/* Global format filter */}
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Format:</Typography>
                {QUEUES.map((q) => (
                    <Chip
                        key={q.key}
                        label={q.label}
                        onClick={() => setSelectedQueue(q.key)}
                        variant={selectedQueue === q.key ? 'filled' : 'outlined'}
                        color={selectedQueue === q.key ? 'primary' : 'default'}
                        size="small"
                    />
                ))}
            </Box>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="Meta" />
                <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label="Player" />
                <Tab icon={<TrophyIcon fontSize="small" />} iconPosition="start" label="Ranking" />
            </Tabs>

            {tab === 0 && <MetaTab selectedQueue={selectedQueue} />}
            {tab === 1 && (
                <PlayerTab
                    selectedQueue={selectedQueue}
                    initialUserId={selectedUserId}
                    onClearInitial={() => setSelectedUserId(null)}
                />
            )}
            {tab === 2 && <RankingTab selectedQueue={selectedQueue} onSelectPlayer={handleSelectPlayer} />}
        </Container>
    );
};

export default Scouting;
